import { FC, memo, useCallback, useMemo, useState } from "react";

import AccessTimeOutlinedIcon from "@mui/icons-material/AccessTimeOutlined";
import { Box, Chip, Stack, Tooltip, Typography, alpha } from "@mui/material";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import tz from "dayjs/plugin/timezone";
import utc from "dayjs/plugin/utc";

import { SATOSContentRender } from "@/components/Application/SATOSContentRender";
import { useTime } from "@/components/Application/TimeContext";
import { Translate } from "@/components/Application/Translate";

import { AlertRequest } from "@/helpers/alerts/AlertRequest";
import { FeedbackCard } from "@/helpers/components/CommentSuggestion/FeedbackCard";

import { ProcedureNodeStep } from "@/model/Procedure/ProcedureNodeStep";
import { ProcedureNodeStepComment } from "@/model/Procedure/ProcedureNodeStepComment";

import { useDeleteProcedureComment } from "@/service/ProcedureMutations";

dayjs.extend(utc);
dayjs.extend(tz);
dayjs.extend(relativeTime);

const clampStyles = (lines: number) => ({
	display: "-webkit-box",
	WebkitLineClamp: lines,
	WebkitBoxOrient: "vertical" as const,
	overflow: "hidden",
	// Soft fade at the bottom for a modern, polished clamp
	maskImage: "linear-gradient(to bottom, black 70%, transparent 100%)",
});

interface StepCommentProps {
	comment: ProcedureNodeStepComment;
	step: ProcedureNodeStep;
	activeSchemeId: number | undefined;
	isOnlyComments?: boolean;
}

export const StepComment: FC<StepCommentProps> = memo((props) => {
	const { comment, step, activeSchemeId, isOnlyComments } = props;

	const [isExpanded, setIsExpanded] = useState(false);
	const { timezone } = useTime();
	const deleteComment = useDeleteProcedureComment();

	const handleDelete = useCallback(
		async (id: number) => {
			const request = {
				commentId: id,
				checklistId: activeSchemeId!,
				stepId: step.Id,
			};
			try {
				// Keeps your existing confirm pattern
				AlertRequest.confirm(
					deleteComment.mutateAsync,
					request,
					"this comment",
				);
			} catch (error) {
				console.error("Failed to delete comment", error);
				AlertRequest.failed();
			}
		},
		[activeSchemeId, step.Id, deleteComment],
	);

	// A bit more space when viewing “only comments”
	const collapsedLines = isOnlyComments ? 6 : 3;

	const createdAt = useMemo(
		() => dayjs.utc(comment.Date).tz(timezone),
		[comment.Date, timezone],
	);

	const exact = useMemo(
		() => createdAt.format("DD/MM/YY, HH:mm:ss"),
		[createdAt],
	);

	const relative = useMemo(() => createdAt.fromNow(), [createdAt]);

	return (
		<FeedbackCard
			accent="info"
			comment={comment}
			title={<Translate String="Procedure.Checklist.Step.Comment.Comment" />}
			isExpanded={isExpanded}
			isDeletable
			onDelete={() => handleDelete(comment.Id)}
			toggleExpand={() => setIsExpanded((v) => !v)}
			chip={
				<Tooltip title={exact} arrow>
					<Chip
						size="small"
						icon={<AccessTimeOutlinedIcon sx={{ fontSize: 16 }} />}
						label={relative}
						variant="outlined"
						sx={{
							borderRadius: 1.5,
							"& .MuiChip-label": { px: 0.75 },
						}}
					/>
				</Tooltip>
			}
		>
			{/* Content (we clamp here so FeedbackCard stays generic) */}
			<Stack spacing={0.75} sx={{ width: "100%" }}>
				<Box sx={isExpanded ? undefined : clampStyles(collapsedLines)}>
					<SATOSContentRender content={comment.Value} />
				</Box>
			</Stack>

			{/* Footer */}
			<Box
				sx={{
					display: "flex",
					justifyContent: "space-between",
					alignItems: "center",
					marginTop: "14px",
				}}
			>
				<Typography variant="body2" color="text.secondary">
					{exact}
				</Typography>
			</Box>
		</FeedbackCard>
	);
});
